import { Box, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { percentage } from "../../shared/percentage";
import { useCurrency } from "../../context/CurrencyContext";
import { Tone, toneColors } from "../general/dataAtoms/shared";

/** A line saying how a figure stands against litigation, and its colour. */
export type VsLitigationLine = { text: string; tone?: Tone };

/**
 * The lines that set a figure against litigation: how far settling moves an
 * amount, favourably above and adversely below; where it leaves an end of
 * the range where it is, how much less likely that is; and a chance in
 * litigation.
 */
export function useVsLitigation() {
  const { t } = useTranslation(undefined, { keyPrefix: "case.shownResults" });
  const { currency } = useCurrency();
  const change = (amount: number): VsLitigationLine => ({
    text: t(amount > 0 ? "above" : amount < 0 ? "below" : "same", {
      amount: currency.format(Math.abs(amount)),
    }),
    tone: amount > 0 ? "favourable" : amount < 0 ? "adverse" : undefined,
  });
  return {
    change,
    end: ({
      change: moved,
      chance,
      without,
    }: {
      change: number;
      chance: number;
      without: number;
    }): VsLitigationLine =>
      moved !== 0
        ? change(moved)
        : {
            text: t("likely", {
              chance: percentage.format(chance),
              without: percentage.format(without),
            }),
          },
    inLitigation: (chance: number) =>
      t("inLitigation", { chance: percentage.format(chance) }),
  };
}

/**
 * Up where settling makes a chance higher than in litigation, down where
 * lower, none where it leaves it: a lean arrow with a triangular head, half
 * the height of the figure it follows and on its baseline, whose tooltip says
 * by how much. `of`
 * names what the chance is of, as the tooltip reads it.
 */
export const ChanceArrow = ({
  chance,
  without,
  of,
}: {
  chance: number;
  without: number;
  of: string;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case.shownResults" });
  // to a hundredth of a percent, as the chances are shown
  const move = Math.sign(Math.round((chance - without) * 1e4));
  if (move === 0) return null;
  const text = t(move > 0 ? "higherChance" : "lowerChance", {
    change: percentage.format(Math.abs(chance - without)),
    of,
  });
  const tones = toneColors(theme);
  return (
    <Tooltip title={text} describeChild>
      {/* drawn, not a character: not every device has a font with one */}
      <Box
        component="svg"
        role="img"
        aria-label={text}
        viewBox="0 0 8 16"
        sx={{
          ml: 0.5,
          height: "0.5em",
          width: "0.25em",
          overflow: "visible",
          color: move > 0 ? tones.favourable : tones.adverse,
          // a lean shaft with a sharp triangular head
          transform: move > 0 ? undefined : "rotate(180deg)",
        }}
      >
        <path d="M4 16V5" stroke="currentColor" strokeWidth={1.5} />
        <path d="M4 0L8 7H0Z" fill="currentColor" />
      </Box>
    </Tooltip>
  );
};
